/**
 * Download a single image file
 */
export async function downloadImage(url: string, filename: string) {
  try {
    // Use API proxy to bypass CORS
    const proxyUrl = `/api/download-image?url=${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      throw new Error(`Failed to download: ${response.statusText}`);
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    throw error;
  }
}

/**
 * Download multiple images as a ZIP file
 * Uses JSZip library for client-side zipping
 */
export async function downloadImagesAsZip(
  images: Array<{
    url: string;
    filename: string;
  }>,
  zipFileName: string = 'images.zip'
) {
  try {
    // Dynamically import JSZip to avoid issues with SSR
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Add each image to the zip
    for (const image of images) {
      try {
        // Use API proxy to bypass CORS
        const proxyUrl = `/api/download-image?url=${encodeURIComponent(image.url)}`;
        const response = await fetch(proxyUrl);

        if (!response.ok) {
          continue;
        }

        const blob = await response.blob();
        zip.file(image.filename, blob);
      } catch (error) {
        // Continue with other images even if one fails
      }
    }

    // Generate and download the zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    throw error;
  }
}
